import { DeleteOutlined, EditOutlined } from "@ant-design/icons";
import { faAdd } from "@fortawesome/pro-duotone-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Badge, Button, Card, DatePicker, Input, message, Select, Table, Tabs } from "antd";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import AddEditFormComponent from "../components/AddEditFormComponent";
import SplitOrderDrawer from "../components/SplitOrderDrawer";
import { fromUtc } from "../helpers/dayjsSetup";
import PurchaseOrderService from "../services/PurchaseOrder/PurchaseOrderService";
import ShipmentService from "../services/Shipment/ShipmentService";

const { Option } = Select;
const { TabPane } = Tabs;

export default function PurchaseOrderEnquiry() {
  const [date, setDate] = useState(null);
  const [qaDate, setQaDate] = useState(null);
  const [purchaseOrder, setPurchaseOrder] = useState(null);
  const [containerCount, setContainerCount] = useState(0);
  const [statusList, setStatusList] = useState(null);
  const [priorityList, setPriorityList] = useState(null);
  const [selectedItems, setSelectedItems] = useState([]);
  const [items, setItems] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSpitOrderDrawerOpen, setIsSplitOrderDrawerOpen] = useState(false);
  const [open, setOpenAddDrawer] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const navigate = useNavigate();
  const [containers, setContainers] = useState([]);
  const [shipments, setShipments] = useState([]);
  const location = useLocation();
  const { orderId } = location.state || {};

  useEffect(() => {
    fetchPurchaseOrderDetails(orderId);
    fetchStatusList();
    fetchPriorityList();
    fetchPurchaseOrderContainers(orderId);
    fetchShipmentDetails(orderId);
  }, []);

  const fetchPurchaseOrderDetails = (orderId) => {
    PurchaseOrderService.fetchPurchaseOrderDetails(orderId)
      .then((response) => {
        if (response.status === 200) {
          setPurchaseOrder(response.data);
          setItems(response.data?.items || []);
          setDate(fromUtc(response.data?.poDate));
          setQaDate(fromUtc(response.data?.qaDate));
        }
      })
      .catch((error) => {
        console.error("Error fetching purchase order details:", error);
      });
  };
  const priorityOptions = [
    { id: 1, label: "Very High" },
    { id: 2, label: "High" },
    { id: 3, label: "Medium" },
    { id: 4, label: "Low" },
    { id: 5, label: "Lowest" },
  ];

  const fetchPriorityList = () => {
    setPriorityList(priorityOptions);
  };

  const handleAddContainer = () => {
    const containerName = `Container ${String.fromCharCode(
      66 + containerCount
    )}`; // B, C, D...

    const newContainer = {
      containerType: containerName,
      number: containerName,
      abn: "2008",
      poItemCount: 0,
      userReference: "user",
    };

    PurchaseOrderService.AddContainerForPO(orderId, newContainer)
      .then((response) => {
        if (response.status === 200 || response.status === 201) {
          message.success(`${containerName} added successfully`);
          fetchPurchaseOrderContainers(orderId);
          setContainerCount((prev) => prev + 1);
        } else {
          message.error(`Failed to add ${containerName}`);
        }
      })
      .catch(() => {
        message.error(`Failed to add ${containerName}`);
      });
  };

  const fetchStatusList = () => {
    PurchaseOrderService.fetchStatusList()
      .then((response) => {
        if (response.status === 200) {
          setStatusList(response.data);
        }
      })
      .catch((error) => {
        console.error("Error fetching status list:", error);
      });
  };

  const fetchPurchaseOrderContainers = (orderId) => {
    PurchaseOrderService.fetchPurchaseOrderContainer(orderId)
      .then((response) => {
        if (response.status === 200) {
          setContainers(response.data);
        }
      })
      .catch((error) => {
        console.error("Error fetching container data:", error);
      });
  };

  const fetchShipmentDetails = (purchaseOrderId) => {
    setIsLoading(true);
    ShipmentService.fetchShipmentDetails(purchaseOrderId)
      .then((response) => {
        setShipments(response.data);
      })
      .catch((err) => {
        message(err.message || "Something went wrong");
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const editPurchaseOrder = () => {
    setIsEditing(true);
  };

  const handleSaveEdit = () => {
    const updatedPurchaseOrder = {
      ...purchaseOrder,
      PurchaseOrderNum: purchaseOrder?.id,
    };

    PurchaseOrderService.updatePurchaseOrder(updatedPurchaseOrder)
      .then(() => {
        message.success("Purchase Order updated successfully");
        setIsEditing(false);
      })
      .catch(() => {
        message.error("Failed to update Purchase Order");
      });
  };

  const removeShipment = (id) => {
    setShipments(shipments.filter((shipment) => shipment.id !== id));
  };

  const shipmentColumns = [
    {
      title: "S. No.",
      dataIndex: "sNo",
      render: (_, record) => record.id,
    },
    {
      title: "Cargo Ready Date",
      dataIndex: "cargoReadyDate",
      render: (value) => (value ? fromUtc(value).format("DD MMM YYYY") : "N/A"),
    },
    { title: "Forwarder", dataIndex: "forwarder" },
    { title: "Port of Loading", dataIndex: "portOfLoading" },
    { title: "Port of Discharge", dataIndex: "portOfDischarge" },
    {
      title: "Action",
      dataIndex: "action",
      render: (_, record) => (
        <Button type="text" danger onClick={() => removeShipment(record.id)}>
          <DeleteOutlined />
        </Button>
      ),
    },
  ];

  const itemColumns = [
    { title: "S. No.", dataIndex: "sNo", render: (_, __, index) => index + 1 },
    { title: "SKU", dataIndex: "sku" },
    { title: "Description", dataIndex: "description" },
    { title: "PO Quantity", dataIndex: "qtyOrdered" },
    { title: "Supply Quantity", dataIndex: "qtyReceived" },
  ];

  const containerColumns = [
    {
      title: "Container Type",
      dataIndex: "containerType",
      render: (_, record) => <Input defaultValue={record.containerType} />,
    },
    {
      title: "Container Number",
      dataIndex: "number",
      render: (_, record) => <Input defaultValue={record.number} />,
    },
    {
      title: "PO Item Count",
      dataIndex: "poItemCount",
      render: (value) => <Badge count={value} />,
    },
  ];

  return (
    <div className="mx-2">
      <div className="flex justify-end gap-2">
        <Button
          onClick={() => {
            const itemsToSplit =
              items?.filter((item) => item.qtyOrdered > item.qtyReceived) || [];
            setSelectedItems(itemsToSplit);
            setIsSplitOrderDrawerOpen(true);
          }}
          type="primary"
          icon={<EditOutlined />}
          disabled={orderId?.includes("_")}
        >
          Split Order
        </Button>

        {!isEditing ? (
          <Button
            type="secondary"
            icon={<EditOutlined />}
            onClick={editPurchaseOrder}
          >
            Edit Order
          </Button>
        ) : (
          <>
            <Button type="primary" onClick={handleSaveEdit}>
              Update
            </Button>
            <Button
              onClick={() => {
                setIsEditing(false);
                fetchPurchaseOrderDetails(orderId);
              }}
            >
              Cancel
            </Button>
          </>
        )}
      </div>

      <Card className="mt-2">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div>
            <label>Purchase Order Number</label>
            <Input value={purchaseOrder?.id} disabled />
          </div>
          <div>
            <label>PO Date</label>
            <DatePicker
              className="w-full"
              value={date}
              onChange={setDate}
              disabled
            />
          </div>
          <div>
            <label>Status</label>
            {isEditing ? (
              <Select
                className="w-full"
                value={purchaseOrder?.statusID ?? undefined}
                onChange={(value) =>
                  setPurchaseOrder((prev) => ({ ...prev, statusID: value }))
                }
              >
                {statusList?.map((status) => (
                  <Option key={status.id} value={status.id}>
                    {status.name}
                  </Option>
                ))}
              </Select>
            ) : (
              <div className="border px-3 py-1 rounded bg-gray-50">
                {statusList?.find((s) => s.id === purchaseOrder?.statusID)
                  ?.name ?? "N/A"}
              </div>
            )}
          </div>

          <div>
            <label>Supplier</label>
            <Input
              value={purchaseOrder?.supplierId}
              disabled
              onChange={(e) =>
                setPurchaseOrder((prev) => ({
                  ...prev,
                  supplierId: e.target.value,
                }))
              }
            />
          </div>
          <div>
            <label>QA Date</label>
            <DatePicker
              className="w-full"
              value={qaDate}
              onChange={setQaDate}
              disabled={!isEditing}
            />
          </div>
          <div>
            <label>Notes</label>
            <Input
              className="w-full"
              onChange={(e) =>
                setPurchaseOrder((prev) => ({ ...prev, notes: e.target.value }))
              }
              value={purchaseOrder?.notes}
              disabled={!isEditing}
            />
          </div>
          <div>
            <label>Priority</label>
            {isEditing ? (
              <Select
                className="w-full"
                value={purchaseOrder?.priorityID ?? undefined}
                onChange={(value) =>
                  setPurchaseOrder((prev) => ({ ...prev, priorityID: value }))
                }
              >
                {priorityList?.map((priority) => (
                  <Option key={priority.id} value={priority.id}>
                    {priority.label}
                  </Option>
                ))}
              </Select>
            ) : (
              <div className="border px-3 py-1 rounded bg-gray-50">
                {priorityList?.find((p) => p.id === purchaseOrder?.priorityID)
                  ?.label ?? "N/A"}
              </div>
            )}
          </div>
        </div>
      </Card>

      <div className="mt-2">
        <Card title="Items">
          <Table columns={itemColumns} dataSource={items} pagination={false} />
        </Card>
      </div>

      <Tabs centered defaultActiveKey="1">
        <TabPane tab="Shipment" key="4">
          <Card
            title="Shipments"
            extra={
              shipments?.length > 0 ? null : (
                <Button
                  icon={<FontAwesomeIcon icon={faAdd} />}
                  onClick={() => setOpenAddDrawer(true)}
                >
                  Add Shipment
                </Button>
              )
            }
          >
            <AddEditFormComponent
              open={open}
              onClose={() => setOpenAddDrawer(false)}
              onSubmit={() => setOpenAddDrawer(false)}
              initialValues={{ purchaseOrderID: purchaseOrder?.id }}
              type={"add"}
              onSuccess={() => fetchShipmentDetails(purchaseOrder?.id)}
              formType="shipment"
            />
            <Table
              columns={shipmentColumns}
              dataSource={shipments}
              rowKey="id"
              pagination={false}
            />
          </Card>
        </TabPane>
        <TabPane tab="Containers" key="1">
          <Card
            title="Containers"
            extra={
              <Button
                icon={<FontAwesomeIcon icon={faAdd} />}
                onClick={handleAddContainer}
              >
                Add Container
              </Button>
            }
          >
            <Table
              columns={containerColumns}
              dataSource={containers}
              rowKey="id"
              pagination={false}
            />
          </Card>
        </TabPane>

        <TabPane tab="Documents" key="3">
          <Card>
            <div>Documents UI will be here.</div>
          </Card>
        </TabPane>

        <TabPane tab="Status History" key="5">
          <Card>No status history available for this purchase order.</Card>
        </TabPane>
      </Tabs>

      <SplitOrderDrawer
        purchaseOrderID={orderId}
        visible={isSpitOrderDrawerOpen}
        onClose={() => setIsSplitOrderDrawerOpen(false)}
        selectedItems={selectedItems}
      />
    </div>
  );
}
